import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { fmtShort } from "@/lib/scheduling";

const Stat = ({ label, value, tone, testId }) => (
  <div className="surface p-4" data-testid={testId}>
    <p className="text-xs uppercase tracking-wide text-ink-500">{label}</p>
    <p className={`mt-1 font-serif text-3xl font-semibold ${tone || ""}`}>{value}</p>
  </div>
);

export const SiteHealth = () => {
  const qc = useQueryClient();
  const { data: h } = useQuery({ queryKey: ["admin", "health"], queryFn: async () => (await api.get("/admin/health")).data, refetchInterval: 60_000 });
  const test = useMutation({
    mutationFn: () => api.post("/admin/health/test-alert"),
    onSuccess: (r) => { qc.invalidateQueries({ queryKey: ["admin", "health"] }); qc.invalidateQueries({ queryKey: ["admin", "outbox"] }); toast.success(`Test alert sent to ${r.data.recipients} Care Director${r.data.recipients === 1 ? "" : "s"}.`); },
    onError: (e) => toast.error(apiError(e)),
  });
  if (!h) return null;
  const hot = h.errors_in_window >= h.threshold;
  return (
    <section id="health" className="mt-12 scroll-mt-24" aria-labelledby="health-heading" data-testid="site-health">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 id="health-heading" className="text-2xl">Site Health</h2>
          <p className="help max-w-[65ch]">Server errors the live site has logged. When {h.threshold} or more land inside {h.window_minutes} minutes, the Care Director gets an email{h.enabled ? "" : " (currently turned off below)"}. Entries hold the request path and error type only, never member details.</p>
        </div>
        <button type="button" className="btn-secondary" onClick={() => test.mutate()} disabled={test.isPending} data-testid="health-test-alert">Send Test Alert</button>
      </div>
      <div className="mt-5 grid gap-4 sm:grid-cols-3">
        <Stat label={`Last ${h.window_minutes} min`} value={h.errors_in_window} tone={hot ? "text-terracotta-600" : ""} testId="health-stat-window" />
        <Stat label="Last 24 hours" value={h.errors_24h} testId="health-stat-24h" />
        <Stat label="Last alert" value={h.last_alert ? fmtShort(h.last_alert.at) : "None yet"} testId="health-stat-last-alert" />
      </div>
      {h.cooldown_until && <p className="help mt-3" data-testid="health-cooldown">Quiet period: no new alert until {fmtShort(h.cooldown_until)}.</p>}
      {h.recent.length > 0 ? (
        <ul className="mt-6 divide-y divide-sage-400/50 border-y border-sage-400/50" data-testid="health-recent">
          {h.recent.map((e) => (
            <li key={e.id} className="grid gap-1 py-3 text-sm sm:grid-cols-12" data-testid={`health-error-${e.id}`}>
              <span className="text-ink-500 sm:col-span-3">{fmtShort(e.at)}</span>
              <span className="font-mono sm:col-span-5 break-all">{e.method} {e.path}</span>
              <span className="sm:col-span-4"><span className="font-semibold">{e.kind}</span>{e.message ? <span className="text-ink-500"> · {e.message}</span> : null}</span>
            </li>
          ))}
        </ul>
      ) : <p className="mt-6 text-ink-500" data-testid="health-empty">No server errors recorded in the last 30 days.</p>}
    </section>
  );
};
